import { AddIcon, Chip, SearchInput, Table, type TableColumns } from "@bp/ui-components";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "urql";
import { getFragmentData, graphql } from "../../client/gql";
import { SortDirection } from "../../client/graphql-client-defs";
import { ProductMark } from "../../components/brand";
import { OrgFormModal } from "../../components/Organizations/modals/OrgFormModal";
import { Page } from "../../components/Page/Page";
import { PageToolbar } from "../../components/PageToolbar/PageToolbar";
import { productsFromClientIds } from "../../utils/product";

// Colocated fragment: the org list only fetches the fields its columns render.
const OrganizationsPage_Organization = graphql(`
  fragment OrganizationsPage_Organization on Organization {
    uuid
    name
    city
    profilesConnection {
      totalCount
    }
    licensedApplicationsConnection {
      edges {
        node {
          clientId
        }
      }
    }
    federatedClientConnection {
      totalCount
      edges {
        node {
          name
          label
        }
      }
    }
  }
`);

const OrganizationsPageQuery = graphql(`
  query OrganizationsPage_Query($options: OrganizationOptions) {
    organizations(options: $options) {
      ...OrganizationsPage_Organization
    }
  }
`);

export function OrganizationsPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [{ data }] = useQuery({
    query: OrganizationsPageQuery,
    variables: { options: { sort: [{ name: SortDirection.Asc }] } },
  });

  const orgs = getFragmentData(OrganizationsPage_Organization, data?.organizations ?? []);
  type OrgRow = (typeof orgs)[number];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) {
      return orgs;
    }
    return orgs.filter(
      (o) => o.name.toLowerCase().includes(s) || (o.city ?? "").toLowerCase().includes(s),
    );
  }, [orgs, q]);

  const columns: TableColumns<OrgRow>[] = [
    {
      id: "name",
      header: "Organisation",
      accessorKey: "name",
      size: 360,
      cell: ({ row }) => (
        <div style={{ minWidth: 0 }}>
          <div>{row.original.name}</div>
          {row.original.city && (
            <div style={{ fontSize: 11, color: "var(--color-grey)" }}>{row.original.city}</div>
          )}
        </div>
      ),
    },
    {
      id: "members",
      header: "Mitglieder",
      accessorFn: (o) => o.profilesConnection?.totalCount ?? 0,
      type: "number",
      alignment: "right",
      size: 120,
    },
    {
      id: "modules",
      header: "Module",
      size: 150,
      cell: ({ row }) => {
        const products = productsFromClientIds(
          (row.original.licensedApplicationsConnection?.edges ?? []).map((e) => e.node?.clientId),
        );
        if (products.length === 0) {
          return <span style={{ color: "var(--color-grey)" }}>—</span>;
        }
        return (
          <div style={{ display: "flex", gap: 6 }}>
            {products.map((p) => (
              <span
                key={p}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  background: "var(--color-grey-lightest)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ProductMark id={p} size={15} />
              </span>
            ))}
          </div>
        );
      },
    },
    {
      id: "sso",
      header: "SSO",
      size: 160,
      cell: ({ row }) => {
        const fed = row.original.federatedClientConnection;
        const node = fed?.edges?.[0]?.node;
        if (!fed || fed.totalCount === 0) {
          return <span style={{ color: "var(--color-grey)" }}>Lokal</span>;
        }
        return <Chip value={node?.name || node?.label || "SSO"} isSmall />;
      },
    },
  ];

  return (
    <Page
      title="Organisationen"
      description="Schulen und Träger als Identity-Tenants. Stammdaten & Verträge liegen im Orga-Portal."
      actions={[
        {
          text: "Organisation anlegen",
          icon: <AddIcon />,
          hierarchy: "primary",
          callback: () => setCreating(true),
        },
      ]}
    >
      {/* Module = aus licensedApplications abgeleitet; SSO = federatedClient.
          „Typ"/„Träger" gibt es im PIM-Schema nicht. */}
      <PageToolbar>
        <SearchInput
          name="org-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Schule oder Ort …"
        />
      </PageToolbar>
      <Table<OrgRow>
        columns={columns}
        data={filtered}
        isDesktop
        showSort
        showRowHover
        showShadow
        showBorderRadius
        onRowClick={(_, row) => navigate(`/organizations/${row.original.uuid}`)}
      />
      {creating && (
        <OrgFormModal
          onClose={() => setCreating(false)}
          onSaved={(uuid) => {
            setCreating(false);
            if (uuid) {
              navigate(`/organizations/${uuid}`);
            }
          }}
        />
      )}
    </Page>
  );
}
